import React, { useRef, useState, useEffect } from "react";
import { useDrop } from "react-dnd";
import { Document, Page, pdfjs } from "react-pdf";
import DraggableResizableField, { FieldData } from "./DraggableResizableField";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFDropZoneProps {
  pdfUrl: string;
  editFields: FieldData[];
  signFields: FieldData[];
  addField: (
    type: string,
    name: string | null,
    top: number,
    left: number,
    width: number,
    height: number,
    page: number,
    target: string
  ) => void;
  editorMode: string;
  handleFieldDelete: (id: number, target: string) => void;
  handleFieldResize: (id: number, width: number, height: number, target: string) => void;
  handleUpdateFields: (
    id: number,
    type: string,
    name: string | null,
    top: number,
    left: number,
    width: number,
    height: number,
    page: number,
    target: string
  ) => void;
  setPdfDimensions: (dimensions: { width: number; height: number }) => void;
  pdfDimensions: { width: number; height: number };
}

const PDFDropZone: React.FC<PDFDropZoneProps> = ({
  pdfUrl,
  editFields,
  signFields,
  addField,
  editorMode,
  handleFieldDelete,
  handleFieldResize,
  handleUpdateFields,
  setPdfDimensions,
}) => {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [droppedField, setDroppedField] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "FIELD",
      drop: (item: any, monitor) => {
        const offset = monitor.getSourceClientOffset();
        if (offset && pdfContainerRef.current) {
          const rect = pdfContainerRef.current.getBoundingClientRect();
          const x = offset.x - rect.left;
          const y = offset.y - rect.top;

          const page = Math.floor((y * numPages) / rect.height) + 1;
          let width = 200;
          let height = 100;
          if (item.type.toUpperCase() === "TEXT") {
            width = 200;
            height = 40;
          }
          setDroppedField({
            type: item.type,
            name: item.name || item.type,
            width: width,
            height: height,
            top: y - (rect.height / numPages) * (page - 1),
            left: x,
            page: page,
            target: editorMode,
          });
        }
      },
      collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    }),
    [editorMode, numPages]
  );

  // Apply the dropped field after rendering
  useEffect(() => {
    if (droppedField) {
      addField(
        droppedField.type,
        droppedField.name,
        droppedField.top,
        droppedField.left,
        droppedField.width,
        droppedField.height,
        droppedField.page,
        droppedField.target
      );
      setDroppedField(null);
    }
  }, [droppedField, addField]);

  const onDocumentLoad = async ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    if (!pdfUrl) return;

    try {
      const loadingTask = pdfjs.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;

      if (!pdf) {
        console.error("PDF document is null");
        setLoading(false);
        return;
      }

      const firstPage = await pdf.getPage(1);

      if (!firstPage) {
        console.error("First page is null");
        setLoading(false);
        return;
      }

      const viewport = firstPage.getViewport({ scale: 2 });
      const { width, height } = viewport;

      setPdfDimensions({ width, height });
      setContainerWidth(width);
      setLoading(false);
    } catch (error) {
      console.error("Error loading PDF:", error);
      setLoading(false);
    }
  };

  return (
    <div
      ref={drop as any}
      style={{
        flex: 1,
        overflow: "auto",
        position: "relative",
        width: `${containerWidth}px`,
        margin: "0 auto",
        backgroundColor: "#f0f0f0",
      }}
    >
      <div ref={pdfContainerRef} style={{ position: "relative" }}>
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoad}>
          {loading ? (
            <div className="flex items-center justify-center h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <>
              {[...Array(numPages)].map((_, index) => {
                const pageNumber = index + 1;
                return (
                  <div
                    key={pageNumber}
                    style={{
                      position: "relative",
                      width: `${containerWidth}px`,
                      marginBottom: "10px"
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={containerWidth}
                      renderAnnotationLayer={true}
                      renderTextLayer={true}
                    />

                    {/* Render sign fields only if in sign mode */}
                    {editorMode === "sign" &&
                      signFields.map((field) => {
                        if (field.page === pageNumber) {
                          return (
                            <DraggableResizableField
                              key={`sign-${field.id}`}
                              field={field}
                              onDrag={(x, y) =>
                                handleUpdateFields(
                                  field.id,
                                  field.type,
                                  field.name,
                                  y,
                                  x,
                                  field.width,
                                  field.height,
                                  pageNumber,
                                  "sign"
                                )
                              }
                              onResize={(width, height) =>
                                handleFieldResize(field.id, width, height, "sign")
                              }
                              onDelete={() => handleFieldDelete(field.id, "sign")}
                            />
                          );
                        }
                        return null;
                      })}

                    {/* Render edit fields only if in edit mode */}
                    {editorMode === "edit" &&
                      editFields.map((field) => {
                        if (field.page === pageNumber) {
                          return (
                            <DraggableResizableField
                              key={`edit-${field.id}`}
                              field={field}
                              fillMode={true}
                              onDrag={(x, y) =>
                                handleUpdateFields(
                                  field.id,
                                  field.type,
                                  field.name,
                                  y,
                                  x,
                                  field.width,
                                  field.height,
                                  pageNumber,
                                  "edit"
                                )
                              }
                              onResize={(width, height) =>
                                handleFieldResize(field.id, width, height, "edit")
                              }
                              onDelete={() => handleFieldDelete(field.id, "edit")}
                              onTextEdit={(text) => {
                                handleUpdateFields(
                                  field.id,
                                  field.type,
                                  text,
                                  field.top,
                                  field.left,
                                  field.width,
                                  field.height,
                                  pageNumber,
                                  "edit"
                                );
                              }}
                              editorMode="edit"
                            />
                          );
                        }
                        return null;
                      })}
                  </div>
                );
              })}
            </>
          )}
        </Document>
      </div>
    </div>
  );
};

export default PDFDropZone;
